import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  Activity,
  BarChart2,
  Cpu,
  Settings,
  LogOut,
  Search,
} from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { useAuthStore } from '../store/useAuthStore';
import { useEntitlements } from '../hooks/useEntitlements';
import { useOSStore, type WorkspaceId } from '../store/useOSStore';
import { Layers } from 'lucide-react';

// ── Module definitions ────────────────────────────────────────────────────────

interface ModuleEntry {
  id: WorkspaceId;
  emoji: string;
  label: string;
  tag: string;
  to: string;
  matchPrefix: string;
  accentColor: string;
  glowColor: string;
  available: boolean;
}

const MODULES: ModuleEntry[] = [
  {
    id: 'diamondedge',
    emoji: '⚾',
    label: 'DiamondEdge',
    tag: 'MLB',
    to: '/diamondedge',
    matchPrefix: '/diamondedge',
    accentColor: 'text-brand',
    glowColor: 'rgba(59,130,246,0.5)',
    available: true,
  },
  {
    id: 'courtedge',
    emoji: '🏀',
    label: 'CourtEdge',
    tag: 'WNBA',
    to: '/courtedge',
    matchPrefix: '/courtedge',
    accentColor: 'text-warn',
    glowColor: 'rgba(245,158,11,0.5)',
    available: true,
  },
  {
    id: 'griironedge',
    emoji: '🏈',
    label: 'GridironEdge',
    tag: 'NFL',
    to: '/griironedge',
    matchPrefix: '/griironedge',
    accentColor: 'text-loss',
    glowColor: 'rgba(239,68,68,0.5)',
    available: false,
  },
  {
    id: 'iceedge',
    emoji: '🏒',
    label: 'IceEdge',
    tag: 'NHL',
    to: '/iceedge',
    matchPrefix: '/iceedge',
    accentColor: 'text-info',
    glowColor: 'rgba(6,182,212,0.5)',
    available: false,
  },
  {
    id: 'pitchedge',
    emoji: '⚽',
    label: 'PitchEdge',
    tag: 'SOC',
    to: '/pitchedge',
    matchPrefix: '/pitchedge',
    accentColor: 'text-win',
    glowColor: 'rgba(16,185,129,0.5)',
    available: false,
  },
  {
    id: 'octagonedge',
    emoji: '🥊',
    label: 'OctagonEdge',
    tag: 'MMA',
    to: '/octagonedge',
    matchPrefix: '/octagonedge',
    accentColor: 'text-purple-bright',
    glowColor: 'rgba(139,92,246,0.5)',
    available: false,
  },
];

// ── Spine button atom ─────────────────────────────────────────────────────────

interface SpineButtonProps {
  active?: boolean;
  disabled?: boolean;
  title: string;
  badge?: number;
  onClick?: () => void;
  children: React.ReactNode;
  accentGlow?: string;
}

function SpineButton({
  active,
  disabled,
  title,
  badge,
  onClick,
  children,
  accentGlow,
}: SpineButtonProps) {
  return (
    <button
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        'relative flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-150',
        active
          ? 'bg-surface-3 border border-surface-4'
          : disabled
            ? 'opacity-25 cursor-not-allowed'
            : 'hover:bg-surface-2 border border-transparent',
      )}
      style={active && accentGlow ? { boxShadow: `0 0 0 1px ${accentGlow}, 0 0 12px ${accentGlow}` } : undefined}
    >
      {children}
      {badge != null && badge > 0 && (
        <span
          className={clsx(
            'absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-[3px]',
            'rounded-full bg-gold border border-surface-1',
            'flex items-center justify-center',
            'text-[8px] font-bold text-surface-0 tabular-nums leading-none',
          )}
        >
          {Math.min(badge, 99)}
        </span>
      )}
    </button>
  );
}

// ── CommandSpine ──────────────────────────────────────────────────────────────

export function CommandSpine() {
  const navigate = useNavigate();
  const location = useLocation();
  const { openCommandBar, intelligenceRailOpen, toggleIntelligenceRail } = useOSStore();
  const { recommendations, unreadCount, connectionStatus } = useAppStore();
  const { user, signOut } = useAuthStore((s) => ({ user: s.user, signOut: s.signOut }));
  const { entitlements } = useEntitlements();

  const userHandle = user?.email?.split('@')[0] ?? '';
  const userInitials = userHandle.slice(0, 2).toUpperCase();
  const plan = entitlements?.plan ?? 'free';

  const isPulseActive =
    location.pathname.startsWith('/pulse') || location.pathname === '/home' || location.pathname === '/';
  const isPortfolioActive = location.pathname.startsWith('/portfolio');
  const isObservatoryActive = location.pathname.startsWith('/observatory');
  const isSettingsActive = location.pathname.startsWith('/settings');

  // Per-module strong bet counts for badge display
  const moduleOpps = (prefix: string) =>
    recommendations.filter(
      (r) =>
        r.decisionStatus === 'STRONG_BET' &&
        ((prefix.includes('mlb') && r.leagueId === 'mlb') ||
          (prefix.includes('wnba') && r.leagueId === 'wnba')),
    ).length;

  const isConnected = connectionStatus === 'connected';

  return (
    <div className="flex flex-col h-full bg-surface-1 border-r border-surface-4 w-12 flex-shrink-0">
      {/* ── Logo / Pulse heartbeat ─────────────────────────────────────────── */}
      <div className="flex items-center justify-center h-8 border-b border-surface-4 flex-shrink-0">
        <div className="relative flex items-center justify-center w-7 h-7">
          <img
            src="/logo.png"
            alt="Navyrx"
            className={clsx(
              'w-6 h-6 object-contain',
              isConnected && 'drop-shadow-[0_0_6px_rgba(139,92,246,0.6)]',
            )}
          />
          {isConnected && (
            <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-purple pointer-events-none" />
          )}
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────────────── */}
      <nav className="flex-1 flex flex-col items-center gap-1 py-2 overflow-y-auto scrollbar-hide px-1.5">

        {/* ⌘K search */}
        <SpineButton title="Command Search (⌘K)" onClick={openCommandBar}>
          <Search className="w-3.5 h-3.5 text-gray-500" />
        </SpineButton>

        {/* Intelligence Rail toggle */}
        <SpineButton
          title="Intelligence Rail — Module Summaries"
          active={intelligenceRailOpen}
          onClick={toggleIntelligenceRail}
        >
          <Layers
            className={clsx(
              'w-3.5 h-3.5 transition-colors',
              intelligenceRailOpen ? 'text-brand' : 'text-gray-500',
            )}
          />
        </SpineButton>

        {/* Pulse Command */}
        <SpineButton
          title="Pulse Command — Intelligence OS"
          active={isPulseActive}
          onClick={() => navigate('/pulse/center')}
          accentGlow="rgba(139,92,246,0.5)"
        >
          <Activity
            className={clsx(
              'w-3.5 h-3.5 transition-colors',
              isPulseActive ? 'text-purple-bright' : 'text-gray-500',
            )}
          />
        </SpineButton>

        <div className="w-5 h-px bg-surface-4 my-1 flex-shrink-0" />

        {/* Module icons */}
        {MODULES.map((mod) => {
          const isActive = location.pathname.startsWith(mod.matchPrefix);
          const oppCount = mod.available ? moduleOpps(mod.matchPrefix) : 0;
          return (
            <SpineButton
              key={mod.id}
              title={`${mod.label} · ${mod.tag}${mod.available ? '' : ' · Coming Soon'}`}
              active={isActive}
              disabled={!mod.available}
              badge={oppCount}
              onClick={() => mod.available && void navigate(mod.to)}
              accentGlow={mod.glowColor}
            >
              <span
                className={clsx(
                  'text-base leading-none select-none transition-all duration-150',
                  !mod.available && 'grayscale opacity-40',
                  isActive && 'drop-shadow-[0_0_6px_var(--tw-shadow-color)]',
                )}
              >
                {mod.emoji}
              </span>
            </SpineButton>
          );
        })}

        <div className="w-5 h-px bg-surface-4 my-1 flex-shrink-0" />

        {/* Portfolio */}
        <SpineButton
          title="Portfolio — Bankroll & Performance"
          active={isPortfolioActive}
          onClick={() => navigate('/portfolio')}
          accentGlow="rgba(16,185,129,0.5)"
        >
          <BarChart2
            className={clsx(
              'w-3.5 h-3.5 transition-colors',
              isPortfolioActive ? 'text-win' : 'text-gray-500',
            )}
          />
        </SpineButton>

        {/* Observatory */}
        <SpineButton
          title="Agent Observatory — Orchestration & Health"
          active={isObservatoryActive}
          onClick={() => navigate('/observatory')}
          accentGlow="rgba(6,182,212,0.5)"
        >
          <Cpu
            className={clsx(
              'w-3.5 h-3.5 transition-colors',
              isObservatoryActive ? 'text-info' : 'text-gray-500',
            )}
          />
        </SpineButton>
      </nav>

      {/* ── Bottom: Settings + Connection + User ─────────────────────────────── */}
      <div className="flex flex-col items-center gap-1.5 pb-2 px-1.5 border-t border-surface-4 pt-2 flex-shrink-0">
        {/* Alerts badge */}
        {unreadCount > 0 && (
          <SpineButton title={`${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}`}>
            <span className="text-[9px] font-bold text-warn tabular-nums">{Math.min(unreadCount, 99)}</span>
          </SpineButton>
        )}

        {/* Settings */}
        <SpineButton
          title="Settings"
          active={isSettingsActive}
          onClick={() => navigate('/settings/profile')}
        >
          <Settings
            className={clsx(
              'w-3.5 h-3.5 transition-colors',
              isSettingsActive ? 'text-gray-200' : 'text-gray-500',
            )}
          />
        </SpineButton>

        {/* Connection dot */}
        <div
          className={clsx(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            connectionStatus === 'connected'
              ? 'bg-win animate-pulse-slow'
              : connectionStatus === 'connecting'
                ? 'bg-warn animate-pulse'
                : 'bg-gray-600',
          )}
          title={`Intelligence ${connectionStatus}`}
        />

        {/* User avatar */}
        {user && (
          <button
            onClick={() => navigate('/settings/profile')}
            title={`${userHandle} · ${plan}`}
            className="relative group"
          >
            <div
              className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                'bg-gradient-to-br from-brand/30 to-brand/15 border border-brand/25',
                'group-hover:border-brand/50 transition-colors',
              )}
            >
              <span className="text-[10px] font-bold text-brand leading-none">{userInitials}</span>
            </div>
          </button>
        )}

        {/* Sign out */}
        <button
          onClick={() => void signOut().then(() => navigate('/'))}
          title="Sign out"
          className="flex items-center justify-center w-9 h-7 rounded-md text-gray-700 hover:text-loss hover:bg-surface-2 transition-colors"
        >
          <LogOut className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
